import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal, computed } from "@angular/core";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { InviteMembersService } from "../../../data/member/invite-members.service";
import { FormControl, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

// Services
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import {
  DatabaseCacheService,
  Section,
} from "../../../data/cache/database-cache.service";
import { ListUsersService } from "../../../data/user/list-users.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { STAFF_INVITATION_I18N } from "./staff-invitation.i18n";

@Component({
  selector: "app-invite-user",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./staff-invitation.component.html",
  styleUrls: ["./staff-invitation.component.scss"],
  imports: [...SHARED_IMPORTS, NgxMatSelectSearchModule],
})
export class StaffInvitationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = STAFF_INVITATION_I18N;

  // section info
  selectedSection: Section;

  email = new FormControl("", [Validators.required, Validators.email]);
  toolbarTitle = this.i18n.title.staffInvite;

  postParam;
  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];
  selectedStatus = "";

  accountGroup = [
    { value: "STAFF", viewValue: this.i18n.accountGroup.staff },
    { value: "USER", viewValue: this.i18n.accountGroup.user },
  ];
  selectedGroup = "";
  selectedRoles = [];

  userType = "";
  userTypeDisp = "";

  /** バックリンクのルート */
  get backRoute(): string[] {
    const listPath = this.userType === 'STAFF' ? 'staff-list' : 'contributor-list';
    return ['/section-admin', this.selectedSection?.sectionId, listPath];
  }

  /** バックリンクのラベル */
  get backLabel(): string {
    return this.userType === 'STAFF'
      ? this.i18n.backLink.staffList
      : this.i18n.backLink.contributorList;
  }

  /** バックリンクのアイコン */
  get backIcon(): string {
    return this.userType === 'STAFF' ? 'people_alt' : 'supervised_user_circle';
  }

  users = [];
  selectedUser;

  // ユーザー検索用
  userSearchCtrl = new FormControl("");
  userSearchQuery = signal("");

  // Loading state
  isLoadingUsers = signal(false);
  isSending = signal(false);

  // フィルタリングされたユーザーリスト
  filteredUsers = computed(() => {
    const query = this.userSearchQuery().toLowerCase().trim();
    if (!query || !this.users) {
      return this.users || [];
    }
    return this.users.filter((user: any) => {
      const name = user.user.code || `${user.user.lastName} ${user.user.firstName}`;
      return name.toLowerCase().includes(query);
    });
  });

  accountManageRole = false;
  userManageRole = false;
  measureRole = false;
  referRole = false;

  jobTitle = "";

  constructor(
    private inviteMembersService: InviteMembersService,
    private router: Router,
    public snackBar: MatSnackBar,
    public storeAuth: StoreAuthService,
    public common: DatabaseCacheService,
    public listUsersService: ListUsersService,
  ) {}

  ngOnInit() {
    const userType = this.common.getUserType(this.router.url);
    this.selectedSection = this.common.getSelectedSection();

    this.userType = userType.userType;
    this.userTypeDisp = userType.userTypeDisp;

    this.toolbarTitle = this.userType === "CONTRIBUTOR"
      ? this.i18n.title.contributorInvite
      : this.i18n.title.staffInvite;

    if (this.userType == "CONTRIBUTOR") {
      this.listUsers();
    }

    // Selected section に関する subscribe
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nodData) => {
      this.selectedSection = this.common.getSelectedSection();
    });

    // ユーザー検索のデバウンス処理
    this.userSearchCtrl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.userSearchQuery.set(value || "");
    });
  }

  getErrorMessage() {
    if (this.email.hasError("required")) {
      return this.i18n.validation.emailRequired;
    }

    return this.email.hasError("email")
      ? this.i18n.validation.emailInvalid
      : "";
  }

  getJobTitleErrorMessage() {
    if (this.jobTitle === "") {
      return this.i18n.validation.jobTitleRequired;
    }
  }

  invite() {
    this.selectedRoles = [];

    //force to set MEASUREMENT and REFER rights for staff account
    this.measureRole = true;
    this.referRole = true;

    if (this.accountManageRole) {
      this.selectedRoles.push("ACCOUNT_MANAGEMENT");
    }
    if (this.userManageRole) {
      this.selectedRoles.push("USER_MANAGEMENT");
    }
    if (this.measureRole) {
      this.selectedRoles.push("MEASUREMENT");
    }
    if (this.referRole) {
      this.selectedRoles.push("REFER");
    }

    if (this.jobTitle == "") {
      return;
    }
    var jobTitleCode = this.common.staffJobTitles.filter(
      (j) => j.desc == this.jobTitle,
    )[0].code;

    this.postParam = {
      email: this.email.value,
      role: this.selectedRoles,
      jobTitleCode: jobTitleCode,
      userType: this.userType.toUpperCase(),
      userId: this.selectedUser,
    };

    let sectionId = this.common.getSelectedSection().sectionId;

    this.isSending.set(true);
    this.inviteMembersService
      .inviteMembers(sectionId, this.postParam)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.isSending.set(false);
          this.snackBar.open(
            this.i18n.message.inviteSuccess,
            this.i18n.button.close,
            {
              duration: 10000,
            },
          );

          switch (this.userType) {
            case "STAFF":
              this.router.navigate([
                "/section-admin/",
                this.selectedSection.sectionId,
                "staff-list",
              ]);
              break;
            case "CONTRIBUTOR":
              this.router.navigate([
                "/section-admin/",
                this.selectedSection.sectionId,
                "contributor-list",
              ]);
              break;
            case "SUPERVISOR":
              this.router.navigate([
                "/section-admin/",
                this.selectedSection.sectionId,
                "supervisor-list",
              ]);
              break;
          }
        },
        (error) => {
          this.isSending.set(false);
          this.snackBar.open(
            this.i18n.message.inviteError,
            this.i18n.button.close,
            {
              duration: 10000,
            },
          );
        },
      );
  }

  resetFields(): void {
    this.email = new FormControl();
    this.accountManageRole = false;
    this.userManageRole = false;
    this.measureRole = false;
    this.referRole = false;
    this.selectedUser = null;
  }

  listUsers() {
    this.isLoadingUsers.set(true);
    this.listUsersService
      .listUserWithUserData(
        this.common.getSelectedSection().sectionId,
        false,
        false,
        true,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.users = res as any;
          this.isLoadingUsers.set(false);
        },
        error: () => {
          this.isLoadingUsers.set(false);
        }
      });
  }
}
